import { useQuery } from '@apollo/client';
import React, { forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';
import Fieldset from './Fieldset';
import Creatable from 'react-select/creatable';
import Select from 'react-select';
import Price from './Price';
import { Badge } from './Tab';
import styled from 'styled-components';
import { ReadOnlyComponent } from './DropdownReadOnlyComponent';

const DropdownRow = styled.div`
  box-sizing: border-box;
  display: flex;
  flex: 0 1 auto;
  flex-direction: row;
  flex-wrap: wrap;

  @media (max-width: 960px) {
    flex-direction: column;
  }
`;

export const MultiMenuList = (props) => {
  const { children, className, cx, getStyles, isMulti } = props;

  if (Array.isArray(children)) {
    return (
      <DropdownRow>
        {children.map((group) => {
          return (
            <Fieldset key={group.key} cols={1 / children.length} style={{ paddingLeft: 0, paddingRight: 0 }}>
              <div
                css={getStyles('menuList', props)}
                className={cx(
                  {
                    'menu-list': true,
                    'menu-list--is-multi': isMulti,
                  },
                  className
                )}
                style={{ boxSizing: null }}
              >
                {group}
              </div>
            </Fieldset>
          );
        })}
      </DropdownRow>
    );
  }

  return children;
};

/**
 * Implementation of latest React-Select that has no query handling of its' own (MultiQueryDropdown covers that).
 */
const DropdownLatest = (props) => {
  const filterString = useRef('');
  const lastKeyCode = useRef(null);

  const valueObject = {
    [props.valueKey]: props.value?.[props.valueKey] ?? props.value,
  };

  const { column, options, runScanQuery } = props;
  const dropdownOptions = column?.options?.dropdown || {};
  const {
    optionRenderer: OptionComponent = props.optionRenderer,
    getGroupLabel = props.getGroupLabel || (() => 'Undefined'),
  } = dropdownOptions;

  const formatGroupLabel = (group) => {
    const groupLabel = getGroupLabel(group);
    return (
      <div>
        {groupLabel} <Badge>{`${group.options?.length}`}</Badge>
      </div>
    );
  };

  const formatOptionLabel = (option) => {
    if (!OptionComponent || (!option.__typename && !option.__isNew__)) {
      //Only want to display the cards in the menu. Does not fit into the control section.
      return (
        <div style={{ display: 'flex' }}>
          <Price>{option.nettPrice}</Price>
        </div>
      );
    }

    const decimals = props.column?.options?.format?.decimals;
    return <OptionComponent option={{ ...option, decimals }} />;
  };

  const handleKeyDown = (event) => {
    lastKeyCode.current = event.keyCode;
    switch (event.keyCode) {
      case 9: // tab
        runScanQuery?.(9, filterString);
        return;
      case 13: // enter
        runScanQuery?.(13, filterString);
        //If we don't stop propagation for the enter key, this dropdown fails to select an entry.
        event.stopPropagation();
        break;
      case 27: // escape
      case 38: // up
      case 40: // down
      case 33: // page up
      case 34: // page down
      case 35: // end key
      case 36: // home key
        event.stopPropagation(); //Stop propagation to prevent grid from capturing incorrectly.
        break;
      default:
        break;
    }
  };

  const onChange = (val, action) => {
    let value = val;

    if (Array.isArray(val) && val.length === 0) value = null;
    if (props.valueOnly) {
      value = val && val[props.valueKey];
    }
    const formatNewValue = props.column?.options?.dropdown?.formatNewValue;

    if (formatNewValue) {
      value = formatNewValue(value);
    }

    props.onChange(value, lastKeyCode.current, { setInactive: true, action });
  };

  const DDComponent = props.creatable ? Creatable : Select;

  if (props.inGrid) {
    return (
      <DDComponent
        components={{
          MenuList: MultiMenuList,
        }}
        isMulti={false}
        onChange={(selectedOption) => {
          if (selectedOption.value) {
            onChange(selectedOption.value);
          } else {
            onChange(selectedOption);
          }
        }}
        onInputChange={(value) => {
          filterString.current = value;
        }}
        options={
          [{ key: 'singleGroup', options }] /*Options nested as a group so we can easily attach a header label*/ || []
        }
        styles={{
          menuPortal: (provided) => ({ ...provided, zIndex: 1000000 }),
          control: (provided) => ({
            ...provided,
            border: 0,
            boxShadow: 'none',
            minHeight: '30px',
            height: '32px',
          }),
          menu: (provided, state) => {
            return {
              ...provided,
              width: `440px`,
            };
          },
          menuList: (provided) => {
            return {
              ...provided,
              maxHeight: window.innerHeight < 700 ? '20vh' : '440px',
            };
          },
          indicatorsContainer: (provided) => {
            return { ...provided, padding: '0px' };
          },
          dropdownIndicator: (provided) => ({
            ...provided,
            padding: '5px',
          }),
          indicatorSeparator: (provided) => ({
            ...provided,
            width: '1px',
            marginTop: '0px',
            marginBottom: '0px',
          }),
          input: (provided, state) => ({
            ...provided,
            margin: '0px',
          }),
        }}
        menuPortalTarget={document.body}
        formatOptionLabel={formatOptionLabel}
        formatGroupLabel={formatGroupLabel}
        onKeyDown={handleKeyDown}
        menuIsOpen={true}
        value={valueObject}
        getOptionValue={(option) => {
          return option?.[props.selectionKey ?? props.valueKey] ?? option;
        }}
        theme={{ borderRadius: 0 }}
        placeholder={props.value}
        autoFocus={true}
        data-testid={props.testId}
        menuPosition={'fixed'}
      />
    );
  }
  //NOTE - Some styling specific to Trade Counter - Need to be careful if using this elsewhere.
  return (
    <DDComponent
      components={{
        MenuList: MultiMenuList,
        ...props.components,
      }}
      isMulti={false}
      onChange={(selectedOption, action) => {
        const value = selectedOption.value || selectedOption;
        onChange(value, action);
      }}
      onInputChange={(value) => {
        filterString.current = value;
      }}
      options={
        [{ key: 'singleGroup', options }] /*Options nested as a group so we can easily attach a header label*/ || []
      }
      styles={{
        menuPortal: (provided) => ({ ...provided, zIndex: 1000000, transform: 'translateX(-262px)' }),
        menu: (provided) => {
          return {
            ...provided,
            width: `440px`,
          };
        },
        menuList: (provided) => {
          return {
            ...provided,
            maxHeight: '400px',
          };
        },
        control: (provided) => ({
          ...provided,
          border: 0,
          boxShadow: 'none',
        }),
        input: (provided) => ({
          ...provided,
          padding: '0px 8px',
          borderBottom: '1px solid #CCC',
          width: '100%',
          textAlign: 'right',
          fontSize: '15px',
          fontWeight: '600',
        }),
        singleValue: (provided) => ({
          ...provided,
          textAlign: 'right',
          //Don't display this when we're using a custom input, e.g. Trade Counter.
          display: props.components?.Input ? 'none' : provided.display,
        }),
        container: (provided) => ({ ...provided, marginTop: '-2px' }),
      }}
      menuPortalTarget={document.body}
      formatOptionLabel={formatOptionLabel}
      formatGroupLabel={formatGroupLabel}
      onKeyDown={handleKeyDown}
      value={props.value}
      theme={{ borderRadius: 0 }}
      placeholder={props.value}
      openMenuOnFocus={true}
      isOptionSelected={(test) => {
        return !test?.manualEntry && test?.nettPrice === props?.value?.nettPrice;
      }}
      creatable={
        props.creatable /*This passthrough is to allow replacement components to determine if the select is creatable - E.g. Trade Counter uses this*/
      }
      data-testid={props.testId}
    />
  );
};

DropdownLatest.propTypes = {
  active: PropTypes.bool,
  clearButton: PropTypes.bool,
  mandatory: PropTypes.bool,
  onChange: PropTypes.func,
  includeLabel: PropTypes.bool,
  inGrid: PropTypes.bool,
  inputPrefix: PropTypes.string,
  readOnly: PropTypes.bool,
  flexBox: PropTypes.bool,
  labelCols: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inputCols: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  id: PropTypes.string,
  hint: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.array, PropTypes.number]),
  validationErrors: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  showWhenNotActive: PropTypes.bool,
  label: PropTypes.string,
  type: PropTypes.string,
  search: PropTypes.bool,
  creatable: PropTypes.bool,
  deletable: PropTypes.bool,
  rawLabel: PropTypes.bool, // option to disable label formatting (e.g. for allcaps fields).
  valueOnly: PropTypes.bool,
};
DropdownLatest.defaultProps = {
  active: false,
  mandatory: false,
  clearButton: false,
  placeholder: '',
  onChange: () => {},
  optionRenderer: null,
  filterOption: null,
  includeLabel: true,
  inGrid: false,
  inputPrefix: null,
  readOnly: false,
  flexBox: true,
  labelCols: 1 / 4,
  inputCols: 3 / 4,
  value: '',
  validationErrors: [],
  hint: '',
  showWhenNotActive: false,
  type: 'text',
  label: '',
  id: '',
  search: false,
  creatable: true,
  deletable: false,
  setInputRef: () => {},
  valueKey: 'value',
  labelKey: 'label',
  dropdown: {},
  components: {},
};
DropdownLatest.displayName = 'Dropdown';

const DropdownLatestScan = (props) => {
  const scanQuery = props.column?.options?.dropdown?.scanQuery;

  const { refetch: scanRefetch } = useQuery(scanQuery, {
    skip: true,
    fetchPolicy: 'network-only',
  });

  const runScanQuery = (key, filterString) => {
    if (!scanQuery) return;
    scanRefetch({
      filter: {
        filterOperation: 'CONTAINS',
        filterParameters: {
          field: 'generic',
          value: filterString.current,
        },
      },
    }).then((response) => {
      if (response?.data?.data) {
        props.onChange(response.data.data, key, { skipTabBehaviour: true });
      }
    });
  };

  return <DropdownLatest {...props} runScanQuery={runScanQuery} />;
};

export default forwardRef((props, ref) => {
  const { readOnly } = props;

  if (readOnly) {
    if (props.type === 'currency') {
      return <Price>{props.value.nettPrice}</Price>;
    }
    return <ReadOnlyComponent ref={ref} {...props} />;
  }

  //Not passing ref to DropdownLatest because it's a function component and not handled currently.
  //Don't think it needs to be, should be better ways to implement similar logic.
  if (props.column?.options?.dropdown?.scanQuery) {
    return <DropdownLatestScan {...props} />;
  }
  return <DropdownLatest {...props} />;
});
